import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Footer() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.1 });

  return (
    <footer ref={ref} className="bg-[#1B4332] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <div 
            className={`md:col-span-1 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <Link to="/" className="flex items-center gap-3 mb-6 group">
              <img 
                src="https://public.readdy.ai/ai/img_res/d60c6eac-2a5e-4b32-8553-ec512239e7e6.png" 
                alt="Best Christmas Gift Reviews" 
                className="h-12 w-auto group-hover:scale-110 transition-transform duration-300"
              />
            </Link>
            <h3 className="text-2xl font-bold mb-4">Best Christmas Gift Reviews</h3>
            <p className="text-gray-300 leading-relaxed mb-6">
              Your trusted source for honest gift reviews and expert recommendations. Making holiday shopping easier and more joyful since 2020.
            </p>
            <div className="flex gap-4">
              {[
                { icon: 'ri-instagram-fill', href: 'https://instagram.com' },
                { icon: 'ri-pinterest-fill', href: 'https://pinterest.com' },
                { icon: 'ri-facebook-fill', href: 'https://facebook.com' },
                { icon: 'ri-twitter-x-fill', href: 'https://twitter.com' }
              ].map((social, index) => (
                <a 
                  key={index}
                  href={social.href} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center hover:scale-125 hover:rotate-12 hover:bg-[#E5C158] transition-all duration-300 cursor-pointer ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <i className={`${social.icon} text-xl`}></i>
                </a>
              ))}
            </div>
          </div>

          <div 
            className={`transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h4 className="text-[#D4AF37] font-bold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-4">
              {[
                { to: '/gift-guides', text: 'Gift Guides' },
                { to: '/reviews', text: 'Product Reviews' },
                { to: '/reviews', text: 'Blog & Tips' },
                { to: '/', text: 'About Us' },
                { to: '/', text: 'Contact' },
                { to: '/affiliate-disclosure', text: 'Affiliate Disclosure' }
              ].map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.to} 
                    className="text-gray-300 hover:text-white hover:translate-x-2 transition-all duration-300 inline-flex items-center gap-2 group"
                  >
                    <i className="ri-arrow-right-s-line opacity-0 group-hover:opacity-100 transition-opacity"></i>
                    {link.text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div 
            className={`transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h4 className="text-[#D4AF37] font-bold text-lg mb-6">Contact</h4>
            <div className="space-y-4 mb-8">
              <a 
                href="mailto:hello@bestchristmasgiftreviews.com" 
                className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors group"
              >
                <i className="ri-mail-fill text-[#D4AF37] group-hover:scale-125 group-hover:rotate-12 transition-transform"></i>
                hello@bestchristmasgiftreviews.com
              </a>
            </div>
            
            <h4 className="text-[#D4AF37] font-bold text-lg mb-4">Browse by Category</h4>
            <ul className="space-y-3">
              {[
                'Gifts for Her',
                'Gifts for Him',
                'Tech Gifts',
                'Luxury Gifts'
              ].map((category, index) => (
                <li key={index}>
                  <Link 
                    to="/gift-guides" 
                    className="text-gray-300 hover:text-white hover:translate-x-2 transition-all duration-300 inline-flex items-center gap-2 group"
                  >
                    <i className="ri-arrow-right-s-line opacity-0 group-hover:opacity-100 transition-opacity"></i>
                    {category}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-[#FFF9F5] border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-600">
            <p className="flex items-center gap-2">
              © 2025 Best Christmas Gift Reviews. All rights reserved.
              <span className="animate-bounce-soft inline-block">🎄</span>
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-gray-600">
              <Link to="/affiliate-disclosure" className="hover:text-[#1B4332] transition-colors cursor-pointer">
                Affiliate Disclosure
              </Link>
              <span className="text-gray-400">|</span>
              <Link to="/privacy-policy" className="hover:text-[#1B4332] transition-colors cursor-pointer">
                Privacy Policy
              </Link>
              <span className="text-gray-400">|</span>
              <Link to="/terms-of-service" className="hover:text-[#1B4332] transition-colors cursor-pointer">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
